import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check, Heart, Cake, Coffee, Sparkles, Home, Users, Dog, Utensils, StickyNote, Shield, Calendar, Car } from "lucide-react";
import CustomerVideoUpload from "@/components/CustomerVideoUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HOT_DRINK_PRESETS = ["Coffee — black", "Coffee — white", "Latte", "Cappuccino", "Americano", "Espresso", "Tea — builder's", "Tea — Earl Grey", "Herbal tea", "Water"];
const VALET_CHOICES = [
  { value: "", label: "No preference" },
  { value: "always", label: "Yes, always give it a valet" },
  { value: "never", label: "No thank you" },
  { value: "ask", label: "Please ask on the day" },
];

export default function PreCallDetailsPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    birthday: "", hot_drink: "", valet_preference: "",
    loan_car_notes: "", collection_address: "", household: "",
    pet_at_home: "", allergies: "", additional_notes: "",
    gdpr_marketing_consent: false, gdpr_share_with_oem_consent: false,
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/jobs/${token}/precall-details`);
        setData(r.data);
        const p = r.data.profile || {};
        const c = r.data.consents || {};
        setForm((f) => ({
          ...f,
          birthday: p.birthday || "",
          hot_drink: p.hot_drink || "",
          valet_preference: p.valet_preference || "",
          loan_car_notes: p.loan_car_notes || "",
          collection_address: p.collection_address || "",
          household: p.household || "",
          pet_at_home: p.pet_at_home || "",
          allergies: p.allergies || "",
          gdpr_marketing_consent: !!c.gdpr_marketing_consent,
          gdpr_share_with_oem_consent: !!c.gdpr_share_with_oem_consent,
        }));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Booking not found");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/public/jobs/${token}/precall-details`, form);
      setSaved(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed — please try again");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="text-center">
          <p className="font-display font-bold text-2xl">Booking not found</p>
          <p className="text-sm text-muted-foreground mt-1">The link may have expired.</p>
        </div>
      </div>
    );
  }

  if (saved) {
    return (
      <div className="min-h-screen grid place-items-center bg-background p-6">
        <div className="max-w-lg text-center card-default p-8" data-testid="precall-thank-you">
          <div className="w-16 h-16 rounded-full grid place-items-center bg-success/15 text-success mx-auto mb-4">
            <Check className="w-8 h-8" />
          </div>
          <div className="font-display font-bold text-2xl">Thank you!</div>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            We&#39;ve got everything we need. See you at <span className="font-semibold">{data.appointment_time}</span> with your {data.vehicle}.
          </p>
        </div>
      </div>
    );
  }

  const firstName = (data.customer_name || "").split(" ")[0];

  return (
    <div className="min-h-screen bg-background">
      <main className="max-w-2xl mx-auto px-4 py-8">
        <header className="mb-6" data-testid="precall-header">
          <div className="label-overline text-primary">Pre-arrival details</div>
          <h1 className="font-display font-bold text-3xl mt-1">
            {firstName ? `Hi ${firstName},` : "Hello,"}
          </h1>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Just a reminder of your appointment with <span className="font-semibold text-foreground">{data.workshop_name}</span>
            {data.appointment_time && <> tomorrow at <span className="font-semibold text-foreground">{data.appointment_time}</span></>}.
            You&#39;re booked in for <span className="font-semibold text-foreground">{data.work_description}</span> on your <span className="font-semibold text-foreground">{data.vehicle} ({data.reg})</span>.
          </p>
        </header>

        <form onSubmit={submit} className="space-y-6">
          <section className="card-default p-5" data-testid="precall-notes-section">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote className="w-4 h-4 text-primary" />
              <h2 className="font-display font-bold text-lg">Anything else we should add to your work order?</h2>
            </div>
            <textarea
              rows={3}
              value={form.additional_notes}
              onChange={(e) => set("additional_notes", e.target.value)}
              placeholder="e.g. 'The passenger door creaks when I open it' — no need if there's nothing to add"
              className="input-default text-sm w-full resize-y"
              data-testid="precall-additional-notes"
            />
          </section>

          <CustomerVideoUpload token={token} customerFirstName={firstName} />

          <section className="card-default p-5" data-testid="precall-concierge-section">
            <div className="flex items-center gap-2 mb-1">
              <Heart className="w-4 h-4 text-warning" />
              <h2 className="font-display font-bold text-lg">A few things to help us look after you</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">All optional — skip anything you&#39;d rather not share.</p>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Your birthday" icon={<Cake className="w-3.5 h-3.5" />}>
                <input type="date" value={form.birthday} onChange={(e) => set("birthday", e.target.value)} className="input-default text-sm" data-testid="precall-birthday" />
              </Field>
              <Field label="Your hot drink of choice" icon={<Coffee className="w-3.5 h-3.5" />}>
                <input list="hot-drink-presets" value={form.hot_drink} onChange={(e) => set("hot_drink", e.target.value)} placeholder="e.g. Latte, no sugar" className="input-default text-sm" data-testid="precall-hot-drink" />
                <datalist id="hot-drink-presets">{HOT_DRINK_PRESETS.map((d) => <option key={d} value={d} />)}</datalist>
              </Field>
              <Field label="Would you like a complimentary valet?" icon={<Sparkles className="w-3.5 h-3.5" />}>
                <select value={form.valet_preference} onChange={(e) => set("valet_preference", e.target.value)} className="input-default text-sm" data-testid="precall-valet">
                  {VALET_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>
              <Field label="Loan car requirements" icon={<Car className="w-3.5 h-3.5" />}>
                <input value={form.loan_car_notes} onChange={(e) => set("loan_car_notes", e.target.value)} placeholder="e.g. child seat, automatic only" className="input-default text-sm" data-testid="precall-loan" />
              </Field>
              <Field label="Collection & delivery address" icon={<Home className="w-3.5 h-3.5" />}>
                <textarea rows={2} value={form.collection_address} onChange={(e) => set("collection_address", e.target.value)} placeholder="Only if you'd like us to collect the car" className="input-default text-sm resize-y" data-testid="precall-address" />
              </Field>
              <Field label="Anyone else in the household?" icon={<Users className="w-3.5 h-3.5" />}>
                <textarea rows={2} value={form.household} onChange={(e) => set("household", e.target.value)} placeholder="e.g. My wife Fiona also uses the car" className="input-default text-sm resize-y" data-testid="precall-household" />
              </Field>
              <Field label="Pet at home (for collection drivers)" icon={<Dog className="w-3.5 h-3.5" />}>
                <input value={form.pet_at_home} onChange={(e) => set("pet_at_home", e.target.value)} placeholder="e.g. friendly labrador" className="input-default text-sm" data-testid="precall-pet" />
              </Field>
              <Field label="Any allergies we should know about?" icon={<Utensils className="w-3.5 h-3.5" />}>
                <input value={form.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="For the biscuit tin & loan-car cleaning products" className="input-default text-sm" data-testid="precall-allergies" />
              </Field>
            </div>
          </section>

          <section className="card-default p-5" data-testid="precall-gdpr-section">
            <div className="flex items-center gap-2 mb-3">
              <Shield className="w-4 h-4 text-primary" />
              <h2 className="font-display font-bold text-lg">Your data preferences</h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
              Under UK GDPR you have complete control. Tick as many or as few as you&#39;d like — you can change your mind at any time by replying to us.
            </p>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/20 cursor-pointer transition-colors mb-2" data-testid="precall-consent-marketing">
              <input type="checkbox" checked={form.gdpr_marketing_consent} onChange={(e) => set("gdpr_marketing_consent", e.target.checked)} className="mt-1 h-4 w-4 rounded" />
              <span className="text-sm leading-relaxed">
                <span className="font-semibold">Marketing communications</span> — I&apos;m happy for {data.workshop_name} to contact me occasionally about service offers, MOT reminders, seasonal checks and events.
              </span>
            </label>
            <label className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/20 cursor-pointer transition-colors" data-testid="precall-consent-oem">
              <input type="checkbox" checked={form.gdpr_share_with_oem_consent} onChange={(e) => set("gdpr_share_with_oem_consent", e.target.checked)} className="mt-1 h-4 w-4 rounded" />
              <span className="text-sm leading-relaxed">
                <span className="font-semibold">Sharing with the manufacturer</span> — I&apos;m happy for {data.workshop_name} to share my details with Toyota (or the relevant manufacturer) for warranty, recall, and product improvement communications.
              </span>
            </label>
          </section>

          <div className="flex items-center justify-between">
            <p className="text-[10px] text-muted-foreground max-w-xs">
              We take your privacy seriously. Data is processed under our Privacy Policy and stored securely.
            </p>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary text-sm inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="precall-submit-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Submit <Check className="w-4 h-4" /></>}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

function Field({ label, icon, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
        {icon}{label}
      </label>
      {children}
    </div>
  );
}
